import React, { useMemo, useState } from 'react';
import { Image, Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import Ionicons from '@expo/vector-icons/Ionicons';
import { displayFont, uiFont } from '../../styles/agapellaFonts';

import { BrandLogo } from '../../components/BrandLogo';
import { STORE_LABEL, STORE_URLS, detectMobileStore, type MobileStore } from '../../components/installBanner';
import { STORE_BADGE } from '../../components/storeBadgeAssets';
import { useCurrentLanguage } from '../../i18n';
import { trackCtaClicked, type AuthState } from '../../services/homeAnalytics';
import { typographyTokens } from '../../styles/typographyTokens';
import { downloadUrl } from '../download/downloadTarget';
import { BADGE_GAP, fitBadgeHeight, showsDownloadQr } from './appDownloadLayout';
const AppDownloadQrCode = React.lazy(() => import('./AppDownloadQrCode'));

/**
 * CORE-1548 — «Agapella всегда рядом»: промоблок приложения на главной.
 *
 * Стоит после «Как работает наша платформа» (ПМ, 17.10.2026). Это
 * не баннер CORE-1116: тот — закрываемая полоса в шапке мобильной веб-версии,
 * а здесь постоянная часть контента, которая на телефоне оказывается ниже
 * первого экрана — так два призыва не делят один экран.
 *
 * Ссылки на сторы — те же `STORE_URLS`, что у баннера: один источник на всё.
 * QR один (как и на макете) и ведёт не в стор, а на нашу страницу `/download`:
 * она сама разводит по системе телефона. Два отдельных кода, по одному на
 * стор, QA завернул 05.10.2026 — человек у экрана не обязан знать, какой из
 * них его.
 *
 * Только веб: в самом приложении звать человека в стор незачем, а телефон на
 * картинке и так его собственный.
 *
 * Возврат QA 16.10.2026 (комментарий 58163): по макету заголовок начинается с
 * логотипа-словознака, за телефоном — акварельный фон с орбитой и «солнцем»,
 * к QR ведёт рисованная стрелка, бейджи стоят в одну строку на любом телефоне.
 */

const phoneImage = require('../../../assets/home/app-download-phone.webp');
/** Фон по мотивам макета: прозрачный SVG, в тёмной теме приглушён. */
const BACKGROUND_SRC = '/home/app-download-bg.svg';
/** Пропорция вырезанного с макета телефона (428 × 865). */
const PHONE_ASPECT = 428 / 865;

/** На телефоне 44 — так оба бейджа умещаются в одну строку на 360 px. */
const BADGE_HEIGHT = { wide: 48, narrow: 44 } as const;

const copy = {
  ru: {
    title: 'Agapella всегда рядом',
    /** Хвост заголовка после словознака «Agapella». */
    titleRest: 'всегда рядом',
    body: 'Практики, Live-сессии и поддерживающее окружение — в вашем телефоне.',
    badge: (store: MobileStore) => `Скачать Agapella в ${STORE_LABEL[store]}`,
    qr: 'QR-код: скачать приложение Agapella',
    qrTitle: 'Скачайте приложение Agapella',
    qrHint: 'Наведите камеру телефона — откроется магазин вашей системы',
    phone: 'Главная Agapella на экране телефона',
    features: ['Практики', 'Live-сессии', 'Сообщества'],
  },
  en: {
    title: 'Agapella is always with you',
    titleRest: 'is always with you',
    body: 'Practices, live sessions and a supportive circle — on your phone.',
    badge: (store: MobileStore) => `Get Agapella on ${STORE_LABEL[store]}`,
    qr: 'QR code: get the Agapella app',
    qrTitle: 'Get the Agapella app',
    qrHint: 'Point your phone camera at the code — your store will open',
    phone: 'Agapella home screen on a phone',
    features: ['Practices', 'Live sessions', 'Community'],
  },
  es: {
    title: 'Agapella siempre contigo',
    titleRest: 'siempre contigo',
    body: 'Prácticas, sesiones en vivo y un entorno de apoyo — en tu teléfono.',
    badge: (store: MobileStore) => `Descargar Agapella en ${STORE_LABEL[store]}`,
    qr: 'Código QR: descargar la app de Agapella',
    qrTitle: 'Descarga la app de Agapella',
    qrHint: 'Apunta la cámara del teléfono al código — se abrirá tu tienda',
    phone: 'Pantalla principal de Agapella en un teléfono',
    features: ['Prácticas', 'Sesiones en vivo', 'Comunidad'],
  },
} as const;

function useCopy(): (typeof copy)[keyof typeof copy] {
  const lang = useCurrentLanguage();
  if (lang.startsWith('en')) return copy.en;
  if (lang.startsWith('es')) return copy.es;
  return copy.ru;
}

const CTA: Record<MobileStore, string> = {
  ios: 'app_download_app_store',
  android: 'app_download_google_play',
};
/** Переход по коду считается одним событием: стор выберет уже страница. */
const QR_CTA = 'app_download_qr';

/** Настоящая ссылка (`<a href>`): новая вкладка, клавиатура и «копировать адрес» — бесплатно. */
const storeLink = (store: MobileStore) => ({
  href: STORE_URLS[store],
  hrefAttrs: { target: '_blank', rel: 'noopener' },
}) as Record<string, unknown>;

interface Props {
  isDark: boolean;
  authState: AuthState;
}

export const AppDownloadSection: React.FC<Props> = ({ isDark, authState }) => {
  const { width } = useWindowDimensions();
  const labels = useCopy();
  // Origin берём текущий: код с дев-стенда должен вести на дев-стенд.
  // Сам QR и его кодировщик загружаются отдельно, только в широком варианте.
  const link = useMemo(
    () => downloadUrl(typeof window !== 'undefined' ? window.location.origin : null),
    [],
  );
  // Ширина ряда бейджей — чтобы оба уместились в строку на любом телефоне.
  const [badgesWidth, setBadgesWidth] = useState(0);

  const s = useMemo(() => styles(isDark), [isDark]);

  if (Platform.OS !== 'web') return null;

  // Планшет и шире — телефон справа, QR под бейджами. На телефоне QR
  // бессмыслен: сканировать нечем, бейдж ведёт в стор напрямую.
  const wide = width >= 768;
  // CORE-1548 (QA 31.10) — ширина решает только композицию: телефон в альбомной
  // ориентации шире планшетного порога, но сканировать код ему по-прежнему нечем.
  // Устройство — по user-agent (iPad на iPadOS — по тачу), как у install-баннера.
  const mobileDevice = typeof navigator !== 'undefined'
    && detectMobileStore(navigator.userAgent, navigator.maxTouchPoints) !== null;
  const showQr = showsDownloadQr({ wide, mobileDevice });
  const badgeHeight = fitBadgeHeight(badgesWidth, wide ? BADGE_HEIGHT.wide : BADGE_HEIGHT.narrow);
  const track = (cta: string) => trackCtaClicked({ cta, authState, section: 'app_download' });

  return (
    <View style={[s.card, wide ? s.wideCard : s.mobileCard]} testID="home-app-download">
      {/* Фон — под содержимым, нажатия не ловит, читалке невидим. */}
      <img
        loading="lazy"
        decoding="async"
        src={BACKGROUND_SRC}
        alt=""
        aria-hidden="true"
        draggable={false}
        data-testid="home-app-download-bg"
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', pointerEvents: 'none', opacity: isDark ? 0.4 : 1,
          ...(!wide ? { opacity: isDark ? 0.75 : 0.65 } : {}),
        }}
      />
      <View style={[s.inner, wide ? s.innerWide : s.innerMobile]}>
        <View style={[s.copy, !wide && s.copyMobile]}>
          {/* Заголовок начинается со словознака, как на макете; для читалки
              строка целая. */}
          <View
            style={[s.titleRow, !wide && s.titleRowMobile]}
            accessibilityRole="header"
            accessibilityLabel={labels.title}
            testID="home-app-download-title"
          >
            <BrandLogo height={wide ? 40 : 30} style={{ flexShrink: 0 }} />
            <Text style={[s.title, !wide && s.titleNarrow]}>{labels.titleRest}</Text>
          </View>
          <Text style={[s.body, !wide && s.bodyMobile]}>{labels.body}</Text>

          {!wide && (
            <View testID="home-app-download-mobile-art" style={s.mobileArt}>
              <View pointerEvents="none" style={s.mobileGlow} />
              <View pointerEvents="none" style={s.mobileOrbit} />
              <View pointerEvents="none" style={s.mobileOrb} />
              <Image source={phoneImage} resizeMode="contain" accessibilityLabel={labels.phone} style={s.phoneMobileArt} />
              <View pointerEvents="none" style={s.mobileFeatures}>
                {labels.features.map((label, index) => (
                  <View key={label} style={[s.mobileFeature, { marginLeft: index === 1 ? 10 : 0 }]}>
                    <Ionicons name={(['leaf-outline', 'videocam-outline', 'people-outline'] as const)[index]} size={17} color={isDark ? '#FFD4A0' : '#745137'} />
                    <Text style={s.mobileFeatureText}>{label}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          <View style={[s.badges, !wide && s.badgesMobile]} onLayout={(e) => setBadgesWidth(e.nativeEvent.layout.width)}>
            {(['ios', 'android'] as const).map((store) => (
              <Pressable
                key={store}
                {...storeLink(store)}
                accessibilityRole="link"
                accessibilityLabel={labels.badge(store)}
                testID={`home-app-download-${store}`}
                onPress={() => track(CTA[store])}
                style={({ pressed }) => [s.badge, pressed && s.pressed]}
              >
                {/* Официальный бейдж целиком, высота общая, ширина — из его
                    собственных пропорций, чтобы ничего не растянуть. */}
                <img
                  loading="lazy"
                  decoding="async"
                  src={STORE_BADGE[store].src}
                  alt=""
                  draggable={false}
                  style={{ height: badgeHeight, width: badgeHeight * STORE_BADGE[store].aspect, display: 'block' }}
                />
              </Pressable>
            ))}
          </View>

          {showQr ? (
            <View style={s.qrRow}>
              <Pressable
                {...({ href: link } as Record<string, unknown>)}
                accessibilityRole="link"
                accessibilityLabel={labels.qr}
                testID="home-app-download-qr"
                onPress={() => track(QR_CTA)}
                style={({ pressed }) => [s.qrTile, pressed && s.pressed]}
              >
                {/* QR всегда тёмный на белом, в любой теме: иначе камера
                    его не прочтёт. Тихая зона — в viewBox. */}
                <React.Suspense fallback={<View style={{ width: QR_SIDE, height: QR_SIDE }} />}>
                  <AppDownloadQrCode link={link} side={QR_SIDE} />
                </React.Suspense>
              </Pressable>
              {/* Рисованная стрелка к коду, как на макете. Чисто декор. */}
              <Svg
                width={44}
                height={44}
                viewBox="0 0 44 44"
                pointerEvents="none"
                testID="home-app-download-arrow"
                style={s.arrow}
                {...({ 'aria-hidden': true } as Record<string, unknown>)}
              >
                <Path d="M38 5 C35 19 27 30 12 36" stroke="#E98245" strokeWidth={2.6} strokeLinecap="round" fill="none" />
                <Path d="M12 36 L22 35.5 M12 36 L16.5 27" stroke="#E98245" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </Svg>
              <View style={s.qrCaption}>
                <Text style={s.qrTitle}>{labels.qrTitle}</Text>
                <Text style={s.qrHint}>{labels.qrHint}</Text>
              </View>
            </View>
          ) : null}
        </View>

        {wide && <Image
          source={phoneImage}
          resizeMode="contain"
          accessibilityLabel={labels.phone}
          style={wide ? s.phoneWide : s.phoneNarrow}
        />}
      </View>
    </View>
  );
};

const QR_SIDE = 112;
const PHONE_HEIGHT_WIDE = 440;
const PHONE_HEIGHT_NARROW = 320;

const styles = (isDark: boolean) => StyleSheet.create({
  card: {
    width: '100%',
    borderRadius: 24,
    borderWidth: 1,
    overflow: 'hidden',
    // Тёплый светлый фон, как у остальной главной; оранжевый — только в
    // акцентах (рамка QR), чтобы бейджи не спорили с фоном.
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#FFF8F2',
    borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(233,130,69,0.18)',
  },
  wideCard: {
    borderColor: isDark ? '#718778' : '#CCD4B5',
    backgroundColor: isDark ? '#304A49' : '#EDF1DC',
    backgroundImage: isDark
      ? 'radial-gradient(ellipse at 86% 56%,rgba(255,190,125,.24),transparent 65%),linear-gradient(120deg,#29464A,#3B534B 60%,#635341)'
      : 'radial-gradient(ellipse at 85% 56%,rgba(255,203,148,.5),transparent 65%),linear-gradient(120deg,#FFFAE8,#DFEAD5 58%,#F5D7B6)',
  } as any,
  inner: {
    padding: 24,
    gap: 24,
    alignItems: 'center',
  },
  innerWide: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 40,
    paddingVertical: 32,
    gap: 32,
  },
  copy: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    gap: 16,
  },
  titleRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 12,
    rowGap: 4,
  },
  title: {
    ...typographyTokens.displayH2,
    color: isDark ? '#F3ECE3' : '#332720',
  },
  titleNarrow: {
    ...displayFont(),
    fontSize: 28,
    lineHeight: 34,
  },
  body: {
    ...typographyTokens.bodyLarge,
    maxWidth: 520,
    color: isDark ? 'rgba(243,236,227,0.78)' : 'rgba(51,39,32,0.78)',
  },
  badges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: BADGE_GAP,
    marginTop: 4,
  },
  badge: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  pressed: {
    opacity: 0.85,
  },
  qrRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
  },
  arrow: {
    alignSelf: 'flex-start',
    marginTop: 6,
  },
  qrTile: {
    alignItems: 'center',
    gap: 6,
    padding: 10,
    borderRadius: 16,
    borderWidth: 1,
    backgroundColor: '#FFFFFF',
    borderColor: 'rgba(233,130,69,0.35)',
  },
  qrLabel: {
    ...typographyTokens.captionAccent,
    color: '#332720',
  },
  qrCaption: {
    flexShrink: 1,
    minWidth: 160,
    gap: 4,
  },
  qrTitle: {
    ...typographyTokens.bodyAccent,
    color: isDark ? '#F3ECE3' : '#332720',
  },
  qrHint: {
    ...typographyTokens.body,
    color: isDark ? 'rgba(243,236,227,0.72)' : 'rgba(51,39,32,0.72)',
  },
  phoneWide: {
    height: PHONE_HEIGHT_WIDE,
    width: PHONE_HEIGHT_WIDE * PHONE_ASPECT,
    flexShrink: 0,
  },
  phoneNarrow: {
    height: PHONE_HEIGHT_NARROW,
    width: PHONE_HEIGHT_NARROW * PHONE_ASPECT,
  },
  mobileCard: {
    borderRadius: 28,
    borderColor: isDark ? '#73867A' : '#C8CBA7',
    backgroundColor: isDark ? '#304D4C' : '#E6EDD8',
    backgroundImage: isDark
      ? 'radial-gradient(ellipse at 90% 70%,rgba(255,183,110,.3),transparent 65%),linear-gradient(145deg,#294D50,#354D49 52%,#655043)'
      : 'radial-gradient(ellipse at 80% 60%,rgba(255,197,132,.65),transparent 65%),linear-gradient(145deg,#FFF7DF,#DBE8CE 52%,#F7CEAA)',
  } as any,
  innerMobile: { paddingHorizontal: 20, paddingTop: 26, paddingBottom: 24, gap: 0 },
  copyMobile: { flex: 0, gap: 14 },
  titleRowMobile: { flexDirection: 'column', flexWrap: 'nowrap', justifyContent: 'center', columnGap: 8, rowGap: 4 },
  bodyMobile: { ...uiFont(), fontSize: 15, lineHeight: 22, textAlign: 'center', color: isDark ? '#E7EBDF' : '#405950' },
  mobileArt: { height: 270, width: '100%', maxWidth: 360, alignSelf: 'center', position: 'relative', marginTop: 4, marginBottom: 4 },
  mobileGlow: { position: 'absolute', right: 2, top: 22, width: 210, height: 210, borderRadius: 105, backgroundColor: isDark ? 'rgba(255,191,113,.18)' : 'rgba(255,251,224,.7)', boxShadow: '0 0 35px rgba(255,195,123,.2)' } as any,
  mobileOrbit: { position: 'absolute', right: -8, top: 16, width: 232, height: 232, borderRadius: 116, borderWidth: 1, borderColor: isDark ? 'rgba(255,210,157,.5)' : 'rgba(166,116,66,.35)' },
  mobileOrb: { position: 'absolute', right: 0, top: 56, width: 14, height: 14, borderRadius: 7, backgroundColor: '#FFD49A', borderWidth: 1, borderColor: '#FFF5D8', boxShadow: '0 0 12px rgba(255,189,110,.65)' } as any,
  phoneMobileArt: { position: 'absolute', right: 12, top: 0, height: 264, width: 264 * PHONE_ASPECT, transform: [{ rotate: '8deg' }] },
  mobileFeatures: { position: 'absolute', left: 0, top: 50, gap: 22, alignItems: 'flex-start' },
  mobileFeature: { flexDirection: 'row', alignItems: 'center', gap: 7, paddingHorizontal: 10, paddingVertical: 9, borderRadius: 14, borderWidth: 1, borderColor: isDark ? 'rgba(255,218,177,.3)' : 'rgba(255,255,239,.85)', backgroundColor: isDark ? '#354E4B' : '#FFFAE9' },
  mobileFeatureText: { ...uiFont(600), fontSize: 12, lineHeight: 17, color: isDark ? '#FFF1D8' : '#365148' },
  badgesMobile: { justifyContent: 'center', flexWrap: 'nowrap', marginTop: 4 },
});
